import { Component, EventEmitter, Input, OnChanges, Output, QueryList, SimpleChanges, ViewChildren } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SHARED_COMPONENTS } from '../../../../../shared/shared-imports';
import { AuthService } from '../../../../../core/services/auth.service';
import { SharedService } from '../../../../../shared/services/shared.service';
import { AttachmentService } from '../../../../../core/services/attachment.service';
import { Utils } from '../../../../../Utils/utils';
import { DocumentServiceType } from '../../../../../shared/constants/services.enum';
import { customValidator, normalizeIban } from '../../../../../Utils/validator';
import { FileComponent } from '../../../../../shared/components/controls/file/file.component';
import { SrStatus } from '../../../../../shared/constants/status.enum';

interface TermsSection {
  title: string;
  items?: string[];
  bulletItems?: string[];
  footer?: string[];
}

interface TermsContent {
  intro?: string[];
  sections?: TermsSection[];
}

const REQUIRED_PRIMARY_VENUE_DOCUMENTS = [
  'trade license',
  'co account details-on bank letterhead',
  'vat registration certificate'
];

@Component({
  selector: 'app-event-form',
  imports: [ReactiveFormsModule, TranslateModule, ...SHARED_COMPONENTS],
  templateUrl: './event-form.component.html',
  styleUrl: './event-form.component.scss'
})
export class EventFormComponent implements OnChanges {
  @ViewChildren('fileCmp') fileComponents!: QueryList<FileComponent>;
  form: FormGroup;
  cancelBookingForm: FormGroup;
  @Input() masterDataLoading = false;
  @Input() submitting = false;
  @Input() noOfSeats = 0;
  @Input() aminities: string[] = [];
  @Input() eventTypeOptions: Array<{ label: string; value: string }> = [];
  @Input() roomSetupOptions: Array<{ label: string; value: string }> = [];
  @Input() cateringOptionOptions: Array<{ label: string; value: string }> = [];
  @Input() cateringTypeOptions: Array<{ label: string; value: string }> = [];
  @Input() reminderOptions: Array<{ label: string; value: string }> = [];
  @Input() cancellationOptions: Array<{ label: string; value: string }> = [];
  @Input() initialData: Record<string, any> | null = null;

  @Input() srFolder: any;
  @Input() mainFromSectionDocs: any[] = [];
  @Input() showActions: boolean = false;
  @Input() srStatus: string = '';

  @Input() technicalErrorMsg: boolean = false;
  @Output() formSubmit = new EventEmitter<any>();
  @Output() saveAndClose = new EventEmitter<void>();
  @Output() progressChange = new EventEmitter<number>();
  @Output() cancelBookingSubmit = new EventEmitter<{ reason: string }>();
  @Output() folderMissing = new EventEmitter<void>();

  primaryAccount: any;
  showCateringFields = false;
  documentServiceType = DocumentServiceType;
  private lastInitialDataRef: Record<string, any> | null = null;
  lang: string = 'en';
  guestEmails: string[] = [];
  guestInputError = '';
  downloadingNoc = false;
  showCancelBookingModal = false;
  showTermsModal = false;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private sharedService: SharedService,
    private attachmentService: AttachmentService,
    private translateService: TranslateService
  ) {
    this.lang = this.sharedService.getCurrentLang();
    this.primaryAccount = this.sharedService.getPrimaryAccount();

    this.form = this.fb.group({
      organiserType: ['other', Validators.required],   // radio
      organiserName: [{ value: this.primaryAccount?.['DC Name English'] || '', disabled: true }, [Validators.required]],
      organiserNameAr: [{ value: this.primaryAccount?.['DC Name Arabic'] || '', disabled: true }, [Validators.required]],
      eventTitle: ['', [Validators.maxLength(280), Validators.required, customValidator('safeText')]],
      email: [this.primaryAccount?.['Main Email Address'] || '', [Validators.required, Validators.email]],
      mobileNumber: [Utils.parseMobile(this.primaryAccount['Main Phone Number'], 'number'), [Validators.required]],
      mobileCountryCode: [Utils.parseMobile(this.primaryAccount['Main Phone Number'], 'cc'), [Validators.required]],
      eventType: ['', [Validators.required]],
      roomSetup: ['', [Validators.required]],
      cateringOption: [''],
      cateringType: [''],
      reminder: ['', [Validators.required]],
      eventInfo: ['', [Validators.maxLength(10000), customValidator('safeText')]],
      attendeeCount: ['', [Validators.required, Validators.min(1), Validators.max(this.noOfSeats)]],
      guestEmail: ['', [customValidator('email')]],
      needParking: [''],
      parkingSlots: ['', [Validators.min(0), Validators.max(this.noOfSeats)]],
      accountHolderName: ['', [Validators.required, Validators.maxLength(350), customValidator('safeText')]],
      ibanNumber: ['', [Validators.required, Validators.maxLength(23), customValidator('iban')]],
      confirmInformation: [false, [Validators.requiredTrue]],
      requestedInfoByAdmin: [''],
      requestedInfoByCustomer: [''],
      reasonForCancellation: ['', { disabled: true }],
      rejectionReason: ['', { disabled: true }],
    });
    this.cancelBookingForm = this.fb.group({
      cancellationReason: ['', Validators.required]
    });
    let loginUserDetail = this.authService.getUserClaims();
    this.form.get('organiserType')?.valueChanges.subscribe((value) => {
      if (value == 'self') {
        const fullName = [loginUserDetail?.given_name, loginUserDetail?.family_name]
          .filter(Boolean)
          .join(' ');
        this.form.get('organiserName')?.setValue(fullName);
        this.form.get('mobileNumber')?.setValue(loginUserDetail?.mobile);
      }
    });

    this.form.get('needParking')?.valueChanges.subscribe((needParking) => {
      const parkingCtrl = this.form.get('parkingSlots');
      if (this.isNeedParkingSelected(needParking)) {
        parkingCtrl?.setValidators([Validators.required, Validators.min(1), this.maxParkingSlotsValidator()]);
      } else {
        parkingCtrl?.setValue('');
        parkingCtrl?.setValidators([Validators.min(0), this.maxParkingSlotsValidator()]);
      }
      parkingCtrl?.updateValueAndValidity();
    });

    this.form.get('attendeeCount')?.valueChanges.subscribe(() => {
      this.form.get('parkingSlots')?.updateValueAndValidity({ emitEvent: false });
    });

    this.form.get('ibanNumber')?.valueChanges.subscribe((value: string | null) => {
      const normalizedValue = normalizeIban(value);
      if (value !== normalizedValue) {
        this.form.get('ibanNumber')?.setValue(normalizedValue, { emitEvent: false });
      }
    });

    this.applyCateringRules();
    this.form.valueChanges.subscribe(() => {
      this.emitProgress();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialData'] && this.initialData && this.lastInitialDataRef !== this.initialData) {
      this.form.patchValue(this.initialData, { emitEvent: false });
      this.cancelBookingForm.patchValue({
        cancellationReason: this.initialData['reasonForCancellation'] ?? ''
      }, { emitEvent: false });
      this.guestEmails = this.parseGuestEmails(this.initialData['guestEmails'] ?? this.initialData['guestEmail']);
      this.form.get('guestEmail')?.setValue('', { emitEvent: false });
      this.lastInitialDataRef = this.initialData;
    }
    this.form.get('attendeeCount')?.setValidators([Validators.required, Validators.min(1), Validators.max(this.noOfSeats)]);
    const parkingCtrl = this.form.get('parkingSlots');
    if (this.isNeedParkingSelected(this.form.get('needParking')?.value)) {
      parkingCtrl?.setValidators([Validators.required, Validators.min(1), this.maxParkingSlotsValidator()]);
    } else {
      parkingCtrl?.setValidators([Validators.min(0), this.maxParkingSlotsValidator()]);
    }
    parkingCtrl?.updateValueAndValidity();
    this.applyCateringRules();
    this.applyFormInteractivity();
    this.emitProgress();
  }

  // This method is used to submit
  submit() {
    const currentGuestInput = String(this.form.get('guestEmail')?.value ?? '').trim();
    if (currentGuestInput) {
      const added = this.addGuest();
      if (!added) {
        return;
      }
    }
    if (this.checkRequiredDocuments()) {
      this.sharedService.toastMessage('ERROR', 'FILL_REQUIRED_FIELDS_DOCUMENTS', 'error', false);
      return;
    }
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    const payload = this.form.getRawValue();
    payload.guestEmail = [...this.guestEmails];
    this.formSubmit.emit(payload);
  }

  onSaveAndClose(): void {
    this.saveAndClose.emit();
  }

  private maxParkingSlotsValidator(): ValidatorFn {
    return (control) => {
      if (!control?.value) {
        return null;
      }

      const attendeeCount = Number(this.form?.get('attendeeCount')?.value ?? 0);
      if (!attendeeCount || Number.isNaN(attendeeCount)) {
        return null;
      }

      const value = Number(control.value);
      if (Number.isNaN(value)) {
        return null;
      }
      return value > attendeeCount ? { maxParkingSlots: true } : null;
    };
  }

  private applyCateringRules(): void {
    const hasCatering = this.aminities.includes('Catering');
    this.showCateringFields = hasCatering;
    const cateringOptionCtrl = this.form.get('cateringOption');
    const cateringTypeCtrl = this.form.get('cateringType');
  }

  isNeedParkingSelected(value: any = this.form?.get('needParking')?.value): boolean {
    if (typeof value === 'boolean') {
      return value;
    }
    const normalized = String(value ?? '').trim().toLowerCase();
    return normalized === 'yes' || normalized === 'y' || normalized === 'true' || normalized === '1';
  }

  addGuest(): boolean {
    const guestControl = this.form.get('guestEmail');
    const guestEmail = String(guestControl?.value ?? '').trim().toLowerCase();
    this.guestInputError = '';

    if (!guestEmail) {
      return false;
    }
    if (!this.isValidEmail(guestEmail)) {
      this.guestInputError = 'Please enter a valid guest email address.';
      return false;
    }
    if (this.guestEmails.includes(guestEmail)) {
      this.guestInputError = 'Guest email is already added.';
      return false;
    }

    this.guestEmails = [...this.guestEmails, guestEmail];
    guestControl?.setValue('');
    guestControl?.markAsPristine();
    guestControl?.markAsUntouched();
    return true;
  }

  removeGuest(index: number): void {
    this.guestEmails = this.guestEmails.filter((_, i) => i !== index);
    this.emitProgress();
  }

  onGuestInputKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter') {
      return;
    }
    event.preventDefault();
    this.addGuest();
  }

  private parseGuestEmails(value: any): string[] {
    if (!value) {
      return [];
    }

    const sourceList = Array.isArray(value) ? value : String(value).split(',');
    const normalized = sourceList
      .map((item: any) => String(item ?? '').trim().toLowerCase())
      .filter((item: string) => !!item)
      .filter((item: string, index: number, arr: string[]) => arr.indexOf(item) === index);

    return normalized;
  }

  private isValidEmail(value: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  }
  handelFileEvent(event: any, documentType: string) {
    this.emitProgress();
  }

  private emitProgress(): void {
    const formRequired = this.getRequiredControlNames();
    const completedRequired = formRequired.filter((name: string) => this.isControlCompleted(name)).length;

    const mandatoryDocs = this.getProgressDocumentTypes()
      .filter((doc: any) => this.isDocumentMandatory(doc))
      .map((doc: any) => String(doc?.nameEn ?? doc?.documentType ?? '').trim())
      .filter((name: string) => !!name);

    const uploadedDocs = (this.srFolder?.documents || [])
      .map((doc: any) => String(doc?.documentType ?? '').trim().toLowerCase());
    const completedDocs = mandatoryDocs.filter((docType: string) => uploadedDocs.includes(docType.toLowerCase())).length;

    const total = formRequired.length + mandatoryDocs.length;
    const done = completedRequired + completedDocs;
    const percent = total > 0 ? Math.round((done / total) * 100) : 0;

    this.progressChange.emit(percent);
  }

  private getRequiredControlNames(): string[] {
    const requiredControls = [
      'organiserName',
      'organiserNameAr',
      'eventTitle',
      'email',
      'mobileNumber',
      'mobileCountryCode',
      'eventType',
      'roomSetup',
      'reminder',
      'attendeeCount',
      'accountHolderName',
      'ibanNumber',
      'confirmInformation'
    ];

    if (this.isNeedParkingSelected(this.form.get('needParking')?.value)) {
      requiredControls.push('parkingSlots');
    }

    return requiredControls;
  }

  private isControlCompleted(controlName: string): boolean {
    const control = this.form.get(controlName);
    if (!control) {
      return false;
    }
    if (control.disabled) {
      return true;
    }
    if (controlName === 'confirmInformation') {
      return control.value === true;
    }
    const value = control.value;
    if (value === null || value === undefined) {
      return false;
    }
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (typeof value === 'string') {
      return value.trim().length > 0 && control.valid;
    }
    if (typeof value === 'number') {
      return !Number.isNaN(value) && control.valid;
    }
    return !!value && control.valid;
  }

  get tradeLicenseDocumentTypes(): any[] {
    return (this.mainFromSectionDocs || []).filter((doc: any) => {
      const normalized = this.normalizeDocName(doc?.nameEn ?? doc?.documentType);
      return REQUIRED_PRIMARY_VENUE_DOCUMENTS.includes(normalized);
    });
  }

  get otherApprovalDocumentTypes(): any[] {
    return (this.mainFromSectionDocs || []).filter((doc: any) => {
      const normalized = this.normalizeDocName(doc?.nameEn ?? doc?.documentType);
      return normalized === 'other' || normalized === 'others' || normalized.includes('other');
    });
  }

  get isNocCertificateApproved(): boolean {
    return String(this.srStatus ?? '').trim() === String(SrStatus.AwaitingFinalPayment ?? '').trim();
  }
  get isPendingApproval(): boolean {
    return String(this.srStatus ?? '').trim() === String(SrStatus.PendingApproval ?? '').trim();
  }
  get isVenueBooked(): boolean {
    return String(this.srStatus ?? '').trim() === String(SrStatus.VenueBooked ?? '').trim();
  }

  get isAwaitingClarification(): boolean {
    return String(this.srStatus ?? '').trim() === String(SrStatus.AwaitingClarification ?? '').trim();
  }

  get showRequestedMoreInfoSection(): boolean {
    return this.isAwaitingClarification;
  }
  get isRejected(): boolean {
    return String(this.srStatus ?? '').trim() === String(SrStatus.Rejected ?? '').trim()
    || String(this.srStatus ?? '').trim() === String(SrStatus.Cancelled ?? '').trim()
    ||String(this.srStatus ?? '').trim() === String(SrStatus.CancellationRequested ?? '').trim();
  }
  get allowOtherApprovalActions(): boolean {
    return this.actionsEnabled || this.isNocCertificateApproved;
  }

  get canSubmit(): boolean {
    return this.actionsEnabled || this.isNocCertificateApproved || this.isAwaitingClarification;
  }

  get submitButtonLabel(): string {
    if (this.isAwaitingClarification) {
      return 'BUTTONS.SUBMIT';
    }
    return this.isNocCertificateApproved
      ? 'VENUE.FORM.SUBMIT_AND_PAY_FINAL_PAYMENT'
      : 'VENUE.FORM.PAY_AND_RESERVE';
  }

  get canShowCancelBookingButton(): boolean {
    return this.isAwaitingClarification;
  }

  get actionsEnabled(): boolean {
    return this.showActions && !this.submitting;
  }

  get hasNocCertificate(): boolean {
    return !!this.findNocDocument();
  }

  onViewNocCertificate(): void {
    this.downloadNocCertificate(true);
  }

  onDownloadNocCertificate(): void {
    this.downloadNocCertificate(false);
  }

  private downloadNocCertificate(openInNewTab: boolean): void {
    const nocDocument = this.findNocDocument();
    if (!nocDocument?.documentId) {
      this.sharedService.toastMessage('ERROR', 'No Objection Certificate not found', 'error');
      return;
    }

    const folderId = String(nocDocument?.memberFolderId ?? this.srFolder?.folderId ?? '').trim();
    if (!folderId) {
      this.sharedService.toastMessage('ERROR', 'Unable to resolve NOC folder', 'error');
      return;
    }

    this.downloadingNoc = true;
    this.sharedService.getEncryption(DocumentServiceType.MEMBERSHIP, {}).subscribe({
      next: (encryptionResponse: any) => {
        const response: any = Utils.decodeAndStringify(encryptionResponse);
        const validation = response?.validation;

        this.attachmentService.downloadFile(String(nocDocument.documentId), folderId, validation).subscribe({
          next: (blob: Blob) => {
            this.downloadingNoc = false;
            const url = window.URL.createObjectURL(blob);
            const fileName = String(
              nocDocument?.name ??
              nocDocument?.label ??
              nocDocument?.documentType ??
              'No_Objection_Certificate.pdf'
            ).trim() || 'No_Objection_Certificate.pdf';

            if (openInNewTab) {
              window.open(url, '_blank');
              setTimeout(() => window.URL.revokeObjectURL(url), 1000);
              return;
            }

            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            link.click();
            window.URL.revokeObjectURL(url);
          },
          error: () => {
            this.downloadingNoc = false;
            this.sharedService.toastMessage('ERROR', 'Unable to download NOC certificate', 'error');
          }
        });
      },
      error: () => {
        this.downloadingNoc = false;
        this.sharedService.toastMessage('ERROR', 'Unable to download NOC certificate', 'error');
      }
    });
  }

  private findNocDocument(): any {
    const documents = this.srFolder?.documents || [];
    const matchTokens = ['no objection', 'no_objection', 'noc'];

    return documents.find((doc: any) => {
      const normalizedType = this.normalizeDocName(doc?.documentType);
      return matchTokens.some((token: string) => normalizedType.includes(token));
    });
  }

  private getProgressDocumentTypes(): any[] {
    const docMap = new Map<string, any>();
    const source = this.isNocCertificateApproved
      ? [...this.otherApprovalDocumentTypes]
      : [...this.tradeLicenseDocumentTypes];
    source.forEach((doc: any) => {
      const key = this.normalizeDocName(doc?.nameEn ?? doc?.documentType);
      if (key && !docMap.has(key)) {
        docMap.set(key, doc);
      }
    });
    return Array.from(docMap.values());
  }

  private checkRequiredDocuments(): boolean {
    const docsToValidate = this.getProgressDocumentTypes();
    if (!docsToValidate.length) {
      return false;
    }

    const fileCmps = this.fileComponents && typeof this.fileComponents.toArray === 'function'
      ? this.fileComponents.toArray()
      : [];

    let hasMissing = false;
    for (const doc of docsToValidate) {
      const documentType = String(doc?.nameEn ?? doc?.documentType ?? '').trim().toLowerCase();
      const cmp = fileCmps.find((item: FileComponent) =>
        String(item?.documentType ?? '').trim().toLowerCase() === documentType
      );
      if (!cmp) {
        continue;
      }
      if (typeof cmp.checkMissing === 'function' && cmp.checkMissing(this.isDocumentMandatory(doc))) {
        hasMissing = true;
      }
    }
    return hasMissing;
  }

  private isDocumentMandatory(doc: any): boolean {
    if (this.isNocCertificateApproved && this.isOtherApprovalDocument(doc)) {
      return true;
    }
    if (this.isPrimaryVenueDocument(doc)) {
      return true;
    }
    return !!doc?.mandatory;
  }

  private isOtherApprovalDocument(doc: any): boolean {
    const normalized = this.normalizeDocName(doc?.nameEn ?? doc?.documentType);
    return normalized === 'other' || normalized === 'others' || normalized.includes('other');
  }

  private isPrimaryVenueDocument(doc: any): boolean {
    const normalized = this.normalizeDocName(doc?.nameEn ?? doc?.documentType);
    return REQUIRED_PRIMARY_VENUE_DOCUMENTS.includes(normalized);
  }

  private applyFormInteractivity(): void {
    if (this.submitting) {
      this.form.disable({ emitEvent: false });
      return;
    }

    if (this.isNocCertificateApproved) {
      this.form.disable({ emitEvent: false });
      this.form.get('confirmInformation')?.setValue(true, { emitEvent: false });
      return;
    }

    if (this.isAwaitingClarification) {
      this.form.disable({ emitEvent: false });
      this.form.get('requestedInfoByCustomer')?.enable({ emitEvent: false });
      this.form.get('requestedInfoByCustomer')?.setValidators([Validators.required, Validators.maxLength(1000), customValidator('safeText')]);

      this.form.get('requestedInfoByAdmin')?.disable({ emitEvent: false });
      this.form.get('confirmInformation')?.setValue(true, { emitEvent: false });
      return;
    }

    if (!this.actionsEnabled) {
      this.form.disable({ emitEvent: false });
      this.form.get('confirmInformation')?.setValue(true, { emitEvent: false });
      return;
    }

    this.form.enable({ emitEvent: false });
    this.form.get('organiserName')?.disable({ emitEvent: false });
    this.form.get('organiserNameAr')?.disable({ emitEvent: false });
  }

  private normalizeDocName(value: any): string {
    return String(value ?? '')
      .trim()
      .toLowerCase()
      .replace(/[_\s]+/g, ' ');
  }

  cancelBooking(): void {
    this.cancelBookingForm.reset({
      cancellationReason: this.initialData?.['reasonForCancellation'] ?? ''
    });
    this.showCancelBookingModal = true;
  }

  closeCancelBookingModal(): void {
    this.showCancelBookingModal = false;
    this.cancelBookingForm.markAsPristine();
    this.cancelBookingForm.markAsUntouched();
  }

  submitCancelBooking(): void {
    if (this.cancelBookingForm.invalid) {
      this.cancelBookingForm.markAllAsTouched();
      return;
    }

    const reason = String(this.cancelBookingForm.get('cancellationReason')?.value ?? '').trim();
    if (!reason) {
      this.cancelBookingForm.markAllAsTouched();
      return;
    }

    this.cancelBookingSubmit.emit({ reason });
    this.closeCancelBookingModal();
  }

  onConfirmInformationClick(event: Event): void {
    const control = this.form.get('confirmInformation');
    if (!control || control.disabled) {
      return;
    }

    if (!control.value) {
      event.preventDefault();
      event.stopPropagation();
      this.openTermsModal();
    }
  }

  openTermsModal(): void {
    this.showTermsModal = true;
  }

  closeTermsModal(): void {
    this.showTermsModal = false;
  }

  confirmTermsAndConditions(): void {
    this.form.get('confirmInformation')?.setValue(true);
    this.form.get('confirmInformation')?.markAsTouched();
    this.closeTermsModal();
  }

  get termsAndConditionsContent(): TermsContent {
    return this.translateService.instant('VENUE.FORM.TERMS_AND_CONDITIONS_CONTENT') as TermsContent;
  }
}
